import { FormProvider, useForm } from 'react-hook-form';
import { Flex, Text } from '@chakra-ui/react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import { generateValidationSchema } from 'src/utils/validation';
import {
  Banner,
  Button,
  ButtonText,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { useAdjudicationFormDraft } from 'src/api/mutations';
import { useSubmitAdjudicationReview } from 'src/api/commands';
import { Recommendation } from 'src/components/PeerReview/Recommendation';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import { useAdjudicationData } from 'src/contexts/AdjudicationDataProvider';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { AssessmentFieldGroup } from 'src/components/Assessment/FieldGroup';
import { IExpertFirstAssessment } from '../../Adjudications/adjudicationType';
interface Props {
  pageText: string;
  subHeading: string;
  onClose: () => void;
}

const AdjudicationFeedbackForm: React.FC<Props> = ({
  onClose,
  pageText,
  subHeading
}) => {
  const {
    adjudication,
    submissionId,
    adjudicationFormSchema,
    submissionContext,
    workflowResponse,
    firstAdjudicationFormDraft,
    invalidateAdjudicationContextData,
    organizationId
  } = useAdjudicationData();
  const ctxId = adjudication.submissionContextId ?? '';
  const toast = useToast();

  const submitReviewMutation = useSubmitAdjudicationReview({
    submissionId,
    organizationId,
    adjudicationId: adjudication.id,
    submissionContextId: ctxId,
    articleWorkflowId: workflowResponse?.articleWorkflow?.id!,
    invalidateReviewContextData: invalidateAdjudicationContextData
  });

  const schemaId =
    workflowResponse.articleWorkflow.formSchemas.reviewerFirstAssessment;
  const adjudicationFormFields = new ReviewFormFields(
    adjudicationFormSchema.documentSchema
  );

  const formDraftMutation = useAdjudicationFormDraft();

  const configs = {
    Originality: adjudicationFormFields.getFieldConfig('Originality'),
    Significance: adjudicationFormFields.getFieldConfig('Significance'),
    Clarity: adjudicationFormFields.getFieldConfig('Clarity'),
    Rigour: adjudicationFormFields.getFieldConfig('Rigour'),
    Impact: adjudicationFormFields.getFieldConfig('Impact'),

    MissingReference: adjudicationFormFields.getFieldConfig('MissingReference'),
    AreaOfExpertise: adjudicationFormFields.getFieldConfig('AreaOfExpertise'),

    Recommendation: adjudicationFormFields.getFieldConfig('Recommendation'),

    FeedbackForAuthors:
      adjudicationFormFields.getFieldConfig('FeedbackForAuthors'),
    AuthorAttachments:
      adjudicationFormFields.getFieldConfig('AuthorAttachments'),
    // RecommendedReviewers: adjudicationFormFields.getFieldConfig('RecommendedReviewers'),
    FeedbackForEditors:
      adjudicationFormFields.getFieldConfig('FeedbackForEditors'),
    EditorAttachments:
      adjudicationFormFields.getFieldConfig('EditorAttachments'),

    // opt-ins
    PeerReviewTransparency: adjudicationFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    ReviewerAnonymity:
      adjudicationFormFields.getFieldConfig('ReviewerAnonymity'),
    WebOfScienceOptIn:
      adjudicationFormFields.getFieldConfig('WebOfScienceOptIn')
  };

  const schema = generateValidationSchema(configs);

  const methods = useForm<IExpertFirstAssessment>({
    defaultValues: firstAdjudicationFormDraft ?? {},
    resolver: ajvResolver(schema)
  });

  const { handleSubmit, getValues } = methods;

  const onSubmit = async () => {
    const formData = getValues();

    const response =
      pageText === 'Prepare Feedback'
        ? await formDraftMutation.mutateAsync({
          entityId: adjudication.id,
          entityType: 'adjudication',
          formType: 'externalAdjudication',
          formSchemaId: schemaId!,
          data: formData
        })
        : await submitReviewMutation.mutateAsync(formData);

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title:
          pageText === 'Prepare Feedback'
            ? 'Feedback Prepared'
            : 'Feedback Submitted',
        description:
          pageText === 'Prepare Feedback'
            ? `You can review and edit your response again via “Prepare Feedback”.`
            : 'The Editors will be notified and will follow up as needed.',
        isClosable: true
      });
      onClose();
    } else {
      toast({
        variant: 'error',
        title: `${pageText} Unsuccessful`,
        description: `We encountered an issue while saving your ${pageText}. Please try again.`,
        isClosable: true
      });
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Overlay height="100%">
          {/* ISSUE: When user open the overlay and they scroll to bottom they can see the page behind the overlay.
                        
                        NOTE: The height 100% in Overlay and below Flex component,
                        is temporary solution to cover available height for overlay. Doing that we can hide the page behind the overlay.
                    */}
          <Flex
            width="100vw"
            flexDirection="column"
            backgroundColor="surfaceSecondaryFill"
          >
            <form onSubmit={handleSubmit(onSubmit, scrollToFirstError)}>
              <Layout width="60rem">
                <Flex
                  flexDirection="column"
                  gap={8}
                  padding={16}
                  paddingBottom={8}
                >
                  <Flex flexDirection="column" gap={2}>
                    <Text textStyle="headingSection">{pageText}</Text>
                    <Text textStyle="textBody">{subHeading}</Text>
                  </Flex>
                  <OutlineCard borderless padding={8} width="100%">
                    <SubmissionInfoCard
                      headingText="SUBMISSION TO ASSESS"
                      showManuscript
                      entryPoint="adjudication"
                    />
                  </OutlineCard>

                  {pageText === 'Prepare Feedback' && (
                    <Banner
                      variant="attention"
                      heading="For your attention"
                      content={`This response can also be edited during, and just before finalizing your decision. Your response will be sent to the Editors once you conclude.`}
                    />
                  )}

                  {configs.Originality &&
                    configs.Clarity &&
                    configs.Significance &&
                    configs.Rigour &&
                    configs.Impact && (
                      <Flex
                        gap={12}
                        padding={6}
                        backgroundColor="surfacePrimaryFill"
                        flexDirection="column"
                      >
                        <AssessmentFieldGroup
                          fieldConfigs={[
                            configs.Originality,
                            configs.Clarity,
                            configs.Significance,
                            configs.Rigour,
                            configs.Impact,
                            configs.MissingReference
                          ]}
                          title="Evaluation Criteria"
                        />
                      </Flex>
                    )}

                  <Flex
                    gap={12}
                    padding={6}
                    backgroundColor="surfacePrimaryFill"
                    flexDirection="column"
                  >
                    {configs.Recommendation && (
                      <Recommendation
                        config={configs.Recommendation}
                        textStyle="headingGroup"
                      />
                    )}
                  </Flex>
                  {configs.FeedbackForEditors && (
                    <Flex flexDirection="column" gap={6}>
                      <Text textStyle="headingGroup">
                        Communications For Editors
                      </Text>
                      <Flex
                        gap={12}
                        padding={6}
                        backgroundColor="surfacePrimaryFill"
                        flexDirection="column"
                      >
                        {configs.FeedbackForEditors && (
                          <CustomFormFieldComponent
                            config={configs.FeedbackForEditors}
                            placeholder="Add feedback to the editors"
                          />
                        )}
                        {configs.EditorAttachments && (
                          <CustomFormFieldComponent
                            config={configs.EditorAttachments}
                            submissionContextId={submissionContext.id}
                            submissionId={adjudication.caseData.submission.id}
                          />
                        )}
                      </Flex>
                    </Flex>
                  )}
                  {configs.FeedbackForAuthors && (
                    <Flex flexDirection="column" gap={6}>
                      <Text textStyle="headingGroup">
                        Communications For Authors
                      </Text>
                      <Flex
                        gap={12}
                        padding={6}
                        backgroundColor="surfacePrimaryFill"
                        flexDirection="column"
                      >
                        {configs.FeedbackForAuthors && (
                          <CustomFormFieldComponent
                            config={configs.FeedbackForAuthors}
                            placeholder="Add feedback to the author"
                          />
                        )}
                        {configs.AuthorAttachments && (
                          <CustomFormFieldComponent
                            config={configs.AuthorAttachments}
                            submissionContextId={submissionContext.id}
                            submissionId={adjudication.caseData.submission.id}
                          />
                        )}
                      </Flex>
                    </Flex>
                  )}
                  {/* Opt Ins */}
                  {(configs.PeerReviewTransparency &&
                    configs.ReviewerAnonymity) ||
                    configs.WebOfScienceOptIn && (
                      <Flex flexDirection="column">
                        <Text textStyle="headingGroup">Opt Ins</Text>
                        <Flex
                          gap={4}
                          paddingX={6}
                          paddingY={4}
                          backgroundColor="surfacePrimaryFill"
                          flexDirection="column"
                        >
                          <AssessmentFieldGroup
                            fieldConfigs={[
                              configs.PeerReviewTransparency,
                              configs.ReviewerAnonymity,
                              configs.WebOfScienceOptIn
                            ]}
                          />
                        </Flex>
                      </Flex>
                    )}
                </Flex>
              </Layout>
              <Flex
                padding={4}
                bottom={0}
                justifyContent="center"
                position="sticky"
                bgColor="surfacePrimaryFill"
              >
                <Flex width="100%" justifyContent="flex-end" gap={4}>
                  <ButtonText variant="caution" onClick={onClose}>
                    Cancel
                  </ButtonText>
                  {pageText === 'Submit Feedback' && (
                    <Button variant="primary" type="submit">
                      Submit Feedback
                    </Button>
                  )}
                  {pageText === 'Prepare Feedback' && (
                    <Button variant="primary" onClick={onSubmit}>
                      Prepare Feedback
                    </Button>
                  )}
                </Flex>
              </Flex>
            </form>
          </Flex>
        </Overlay>
      </FormProvider>
    </>
  );
};

export default AdjudicationFeedbackForm;
